// #308: the events feed is a flat list of talks, but a multi-day conference
// should read as ONE card holding several talks rather than several unrelated
// rows. This groups a page of events into renderable rows.
//
// Grouping is done client-side on the loaded items rather than by fetching
// /api/v1/conferences separately, for two reasons: the events list already
// carries each talk's nested conference summary, and it keeps the existing
// filters (speaker, tag, country, date range) working untouched — a separate
// conference fetch would silently ignore them.
//
// Consequence worth knowing: because pagination slices the flat list, a
// conference whose talks straddle a page boundary shows only the talks loaded
// so far, and gains the rest as later pages append. The count in the rail is
// therefore "talks loaded", which is why the card links through to the
// conference page for the authoritative programme.

// Rows come back in one of two shapes:
//   { type: 'conference', id, conference, talks: [...] }
//   { type: 'talk', id, event }
//
// Order follows the earliest talk in each group, so a conference sits where its
// first session falls and the feed stays chronological.
export function groupByConference(events) {
  if (!Array.isArray(events)) return []

  const rows = []
  const byConferenceId = new Map()

  for (const event of events) {
    const conference = event?.conference
    // A talk with no conference is standalone and renders exactly as before.
    // Guard on the object shape too: a string here means a pre-#307 API, and
    // that talk has no conference identity to group on.
    if (!conference || typeof conference !== 'object' || !conference.id) {
      rows.push({ type: 'talk', id: event.id, event })
      continue
    }

    const existing = byConferenceId.get(conference.id)
    if (existing) {
      existing.talks.push(event)
      continue
    }

    const row = {
      type: 'conference',
      id: conference.id,
      conference,
      talks: [event],
    }
    byConferenceId.set(conference.id, row)
    rows.push(row)
  }

  // Talks within a conference are ordered by start time; the API returns them
  // that way for a conference detail read, but a filtered event feed can
  // interleave them, so sort defensively.
  for (const row of rows) {
    if (row.type === 'conference') {
      row.talks.sort((a, b) => String(a.startsAtUtc).localeCompare(String(b.startsAtUtc)))
    }
  }

  return rows
}

// The conference summary nested in an event carries the container's dates and
// venue but not its talks. ConferenceCard wants one object, so fold the grouped
// talks in.
export function toConferenceWithTalks(row) {
  return { ...row.conference, talks: row.talks }
}

// #334: the tags to show at the top of a conference card.
//
// `ConferenceSummaryDto` deliberately carries no tags — it exists so an event
// list does not repeat every conference's tags and abstract on every row — so
// `conference.tags` is `undefined` for every card in the feed and the card
// rendered none. The tags that matter are on the talks anyway: they describe
// what is actually being said. Gather them from the talks (and from the
// conference itself, for the detail-page shape that does carry them),
// de-duplicated by slug and in first-seen order, which is chronological
// because the talks are already sorted.
export function conferenceTags(conference) {
  const seen = new Set()
  const out = []
  const add = (tag) => {
    const slug = tag?.slug || tag?.name
    if (!slug || seen.has(slug)) return
    seen.add(slug)
    out.push(tag)
  }
  for (const tag of conference?.tags || []) add(tag)
  for (const talk of conference?.talks || []) {
    for (const tag of talk?.tags || []) add(tag)
  }
  return out
}
